import DocumentConnection, { DocumentConnectionStatus } from 'models/documentConnection';
import { DocumentProviderType } from 'models/documentProvider';
import { apiGetDocumentConnections } from 'services/Api/documentProviderService';
import { apiRequest } from 'services/Auth/authConfig';
import { EntityCache, ICache } from './entityCache';

/**
 * The tenant's connections to document providers. Holds no credentials: the server returns
 * the name, the provider and whether a secret is stored, never the secret itself.
 */
export class DocumentConnectionCache
  extends EntityCache<DocumentConnection>
  implements ICache<DocumentConnection, DocumentConnectionCache>
{
  private isLoading: boolean = false;

  private connectionsPromise: Promise<DocumentConnection[]> | undefined;

  constructor() {
    super();
    this.getId = (connection) => connection.documentConnectionId;
    this.getEmptyItem = () => new DocumentConnection();
  }

  /** The connections a new category may be created on: enabled and of this provider. */
  getUsable(providerType?: DocumentProviderType): DocumentConnection[] {
    return (this.items ?? []).filter(
      (c) =>
        c.status === DocumentConnectionStatus.Enabled &&
        (providerType === undefined || c.providerType === providerType),
    );
  }

  async getItems(refresh: boolean = false): Promise<DocumentConnection[]> {
    if (this.isLoading && this.connectionsPromise) {
      return await this.connectionsPromise;
    }

    if (this.items.length === 0 || refresh) {
      if (this.appContext && !this.isLoading) {
        try {
          this.isLoading = true;
          const accessToken = await this.appContext.getAccessToken(apiRequest.scopes);
          this.connectionsPromise = apiGetDocumentConnections(accessToken);
          this.items = await this.connectionsPromise;
        } catch (err) {
          this.appContext.setError(err);
        } finally {
          this.connectionsPromise = undefined;
          this.isLoading = false;
        }
      } else if (!this.appContext) {
        this.items = [];
      }
    }

    return this.items;
  }

  clone(): DocumentConnectionCache {
    const cache: DocumentConnectionCache = new DocumentConnectionCache();
    cache.items = this.items?.map((c) => c.clone());

    return cache;
  }
}
